import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  ArrowRight,
  ChevronRight,
  Check,
  Utensils,
  Truck,
  Briefcase,
  Store,
  Tag,
  MapPin,
  MousePointerClick,
  Settings2,
  ShieldCheck,
  Users,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { AnywhereIllustration } from "@/components/illustrations/FintechIllustrations";


/* ────────────────────────────────────────────────────────────
   MOBILE APP DATA — 3 stacked feature sections
   ──────────────────────────────────────────────────────────── */
const MOBILE_APPS = [
  {
    slug: "valor-pay",
    brand: "Valor Pay",
    title: "Valor Pay",
    description:
      "Valor Pay gives businesses a flexible mobile payment experience with tools to accept payments, manage activity, send invoices, and stay connected to transactions from anywhere.",
    features: [
      "Real-Time Transactions",
      "Capture Signatures",
      "Virtual Terminal",
      "Email and SMS Invoicing",
      "In-App Batch Notifications",
      "RCKT Mobile POS",
    ],
    accent: "Mobile App",
  },
  {
    slug: "dejavoo-iposgo",
    brand: "Dejavoo iPOSgo",
    title: "Dejavoo iPOSgo",
    description:
      "Dejavoo iPOSgo helps businesses accept secure mobile payments without relying on traditional countertop hardware. It gives merchants a fast, private, and convenient way to process payments from a mobile device.",
    features: [
      "No Hardware Needed",
      "Secure & Private Transactions",
      "Faster Checkout",
      "High ROI",
      "Lower Interchange",
      "Lower Chargeback Risks",
    ],
    accent: "Mobile App",
  },
  {
    slug: "clover-go",
    brand: "Clover Go",
    title: "Clover Go",
    description:
      "Clover Go gives businesses a simple mobile POS solution for accepting payments on the go. It is built for flexible selling, quick checkout, digital receipts, and secure mobile payment acceptance.",
    features: [
      "Mobile POS System",
      "Chip, Dip & Contactless Payments",
      "Digital Receipts",
      "Flexible Order Management",
      "Intuitive Clover Go App",
      "Security & Support",
    ],
    accent: "Mobile POS",
  },
];

/* ────────────────────────────────────────────────────────────
   INDUSTRIES / USE CASES
   ──────────────────────────────────────────────────────────── */
const USE_CASES = [
  {
    icon: Utensils,
    title: "Restaurants & Tableside Payments",
    text: "Accept payments at the table and improve checkout convenience for dine-in customers.",
  },
  {
    icon: Truck,
    title: "Food Trucks & Events",
    text: "Take secure payments on the go with flexible mobile checkout tools.",
  },
  {
    icon: Briefcase,
    title: "Delivery & Service Businesses",
    text: "Accept payments in the field, at the customer’s location, or on delivery.",
  },
  {
    icon: Store,
    title: "Retail Line-Busting",
    text: "Speed up checkout and reduce wait times during busy periods.",
  },
  {
    icon: Tag,
    title: "Pop-Up Shops",
    text: "Use portable mobile payment solutions for temporary and mobile selling environments.",
  },
  {
    icon: MapPin,
    title: "Mobile Service Providers",
    text: "Perfect for businesses that need to accept payments wherever the job takes them.",
  },
];

/* ────────────────────────────────────────────────────────────
   HOW IT WORKS — 4 STEPS
   ──────────────────────────────────────────────────────────── */
const STEPS = [
  {
    icon: MousePointerClick,
    title: "Choose your mobile payment option",
  },
  {
    icon: Settings2,
    title: "Get configured with Swipetronics",
  },
  {
    icon: ShieldCheck,
    title: "Accept payments securely",
  },
  {
    icon: Users,
    title: "Serve customers anywhere",
  },
];

/* ────────────────────────────────────────────────────────────
   PAYMENT METHODS DATA — 8 boxes
   ──────────────────────────────────────────────────────────── */
const PAYMENT_METHODS = [
  {
    slug: "apple-pay",
    name: "Apple Pay",
    description:
      "With Apple Pay, customers can complete transactions quickly and securely with just a touch.",
    img: "/payment-methods/Apple-Pay-1.png",
  },
  {
    slug: "google-pay",
    name: "Google Pay",
    description:
      "Customers can make payments conveniently using Google Pay, offering a secure and fast way to complete transactions.",
    img: "/payment-methods/Google-Pay.svg",
  },
  {
    slug: "valor-pay",
    name: "ValorPay",
    description:
      "Our ValorPay system provides a seamless payment experience, integrating effortlessly with your business and delighting your customers.",
    img: "/payment-methods/Valor-Pay.svg",
  },
  {
    slug: "garmin-pay",
    name: "Garmin Pay",
    description:
      "Stay ahead with Garmin Pay, providing your customers with an additional easy and secure payment method.",
    img: "/payment-methods/Garmin-Pay.svg",
  },
  {
    slug: "ach-bank",
    name: "ACH & Bank Transfers",
    description:
      "ACH and bank transfers provide a secure, efficient way to transfer funds directly between bank accounts, ideal for reducing processing fees. They are useful for both one-time and recurring payments.",
    img: "/payment-methods/ach-bank-v3.png",
  },
  {
    slug: "sms-paynow",
    name: "SMS & PayNow Links",
    description:
      "SMS and PayNow links make payments easier by allowing customers to complete secure transactions from their phone with just a few taps.",
    img: "/payment-methods/SMS.png",
  },
  {
    slug: "qr-codes",
    name: "QR Codes",
    description:
      "QR codes give customers a fast way to scan, pay, access websites, and complete transactions conveniently from a smartphone.",
    img: "/payment-methods/qr-code-v3.png",
  },
  {
    slug: "invoice-recurring",
    name: "Invoice & Recurring Billing",
    description:
      "Invoicing helps automate billing and track payments, while recurring billing supports scheduled transactions for ongoing services.",
    img: "/payment-methods/Invoice-image.png",
  },
];

/* ────────────────────────────────────────────────────────────
   APP STORE / GOOGLE PLAY BUTTONS
   ──────────────────────────────────────────────────────────── */
function StoreButton({ store }) {
  if (store === "apple") {
    return (
      <a
        href="/contact"
        className="group inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-[var(--swipe-navy)] text-white border border-white/10 hover:bg-black hover:-translate-y-0.5 transition-all duration-300 shadow-[0_14px_28px_-12px_rgba(6,26,58,0.55)]"
        aria-label="Download on the App Store"
      >
        {/* Apple logo */}
        <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true" fill="currentColor">
          <path d="M16.365 1.43c0 1.14-.49 2.27-1.27 3.06-.83.84-2.17 1.49-3.27 1.4-.13-1.1.4-2.27 1.16-3.05.83-.86 2.27-1.5 3.38-1.41Zm3.97 17.07c-.55 1.27-.81 1.84-1.51 2.97-.99 1.59-2.39 3.58-4.13 3.59-1.54.02-1.94-1.01-4.04-1-2.1.01-2.54 1.02-4.09 1-1.74-.01-3.06-1.8-4.06-3.39-2.78-4.42-3.07-9.6-1.36-12.36 1.22-1.96 3.13-3.11 4.94-3.11 1.84 0 2.99 1.01 4.52 1.01 1.48 0 2.39-1.01 4.52-1.01 1.61 0 3.31.87 4.53 2.38-3.98 2.18-3.34 7.86 1.18 9.92Z" />
        </svg>
        <span className="text-left leading-tight">
          <span className="block text-[9.5px] uppercase tracking-[0.18em] font-bold text-white/65">
            Download on the
          </span>
          <span className="block text-[15px] font-extrabold tracking-tight">App Store</span>
        </span>
      </a>
    );
  }
  return (
    <a
      href="/contact"
      className="group inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-[var(--swipe-navy)] text-white border border-white/10 hover:bg-black hover:-translate-y-0.5 transition-all duration-300 shadow-[0_14px_28px_-12px_rgba(6,26,58,0.55)]"
      aria-label="Get it on Google Play"
    >
      {/* Google Play logo */}
      <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
        <defs>
          <linearGradient id="gp-a" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00C7E1" />
            <stop offset="100%" stopColor="#005ECB" />
          </linearGradient>
          <linearGradient id="gp-b" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF3A44" />
            <stop offset="100%" stopColor="#C31162" />
          </linearGradient>
          <linearGradient id="gp-c" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFD300" />
            <stop offset="100%" stopColor="#FF8E00" />
          </linearGradient>
          <linearGradient id="gp-d" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00A95C" />
            <stop offset="100%" stopColor="#00853F" />
          </linearGradient>
        </defs>
        <path d="M3.3 2.1c-.2.2-.3.6-.3 1V20.9c0 .4.1.7.3 1l9.3-10L3.3 2.1Z" fill="url(#gp-a)" />
        <path d="M16.5 8.2 4.3 1.4c-.4-.2-.7-.2-1 0L12.6 12l3.9-3.8Z" fill="url(#gp-b)" />
        <path d="M16.5 15.8 12.6 12l-9.3 10c.3.1.6.2 1 0l12.2-6.2Z" fill="url(#gp-d)" />
        <path d="m16.5 8.2-3.9 3.8 3.9 3.8 3.6-2c.9-.5.9-1.8 0-2.3l-3.6-3.3Z" fill="url(#gp-c)" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[9.5px] uppercase tracking-[0.18em] font-bold text-white/65">
          Get it on
        </span>
        <span className="block text-[15px] font-extrabold tracking-tight">Google Play</span>
      </span>
    </a>
  );
}

/* ────────────────────────────────────────────────────────────
   PHONE MOCKUP — realistic, brand-specific app screenshots
   ──────────────────────────────────────────────────────────── */
function PhoneMockup({ variant }) {
  const id = `pm-${variant}`;

  // Brand-specific theme
  const theme = {
    "valor-pay": {
      name: "Valor Pay",
      tag: "V",
      primary: "#0072FF",
      accent: "#3A8EFF",
      headerGradient: ["#0A2552", "#0072FF"],
      tagline: "Payments. Anywhere.",
    },
    "dejavoo-iposgo": {
      name: "iPOSgo",
      tag: "D",
      primary: "#0072FF",
      accent: "#3A8EFF",
      headerGradient: ["#061A3A", "#0072FF"],
      tagline: "Mobile Checkout",
    },
    "clover-go": {
      name: "Clover Go",
      tag: "C",
      primary: "#0072FF",
      accent: "#3A8EFF",
      headerGradient: ["#0A2552", "#0072FF"],
      tagline: "Sell. Anywhere.",
    },
  }[variant];

  return (
    <div className="relative w-full max-w-[480px] mx-auto" data-testid={`phone-mockup-${variant}`}>
      {/* glow halo */}
      <div
        aria-hidden="true"
        className="absolute inset-x-8 top-10 bottom-10 rounded-[60px] pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,114,255,0.32) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      <svg
        viewBox="0 0 480 640"
        className="relative w-full h-auto"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`${id}-frame`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1A1F2E" />
            <stop offset="50%" stopColor="#0F1420" />
            <stop offset="100%" stopColor="#06080F" />
          </linearGradient>
          <linearGradient id={`${id}-bezel`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3A4150" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#000814" stopOpacity="0" />
            <stop offset="100%" stopColor="#3A4150" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id={`${id}-screen`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FAFCFF" />
            <stop offset="100%" stopColor="#EAF3FF" />
          </linearGradient>
          <linearGradient id={`${id}-header`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={theme.headerGradient[0]} />
            <stop offset="100%" stopColor={theme.headerGradient[1]} />
          </linearGradient>
          <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={theme.primary} />
            <stop offset="100%" stopColor={theme.accent} />
          </linearGradient>
          <linearGradient id={`${id}-glare`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.18" />
            <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <filter id={`${id}-deep-shadow`} x="-30%" y="-20%" width="160%" height="140%">
            <feDropShadow dx="0" dy="40" stdDeviation="30" floodColor="#061A3A" floodOpacity="0.30" />
          </filter>
          <filter id={`${id}-card-shadow`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="14" stdDeviation="18" floodColor="#061A3A" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* SIDE DEVICE — Clover Go reader behind/beside the phone */}
        {variant === "clover-go" && (
          <g transform="translate(296 320) rotate(10)" filter={`url(#${id}-card-shadow)`}>
            {/* reader body */}
            <rect x="-12" y="-22" width="200" height="138" rx="26" fill="#1A1F2E" />
            <rect x="-6" y="-16" width="188" height="126" rx="20" fill="#0F1420" />
            {/* top edge highlight */}
            <rect x="-6" y="-16" width="188" height="2" rx="1" fill="#FFFFFF" fillOpacity="0.12" />
            {/* clover green leaf logo at top */}
            <g transform="translate(76 4)">
              <circle cx="0" cy="0" r="10" fill="#00C853" />
              <circle cx="0" cy="-8" r="5" fill="#00C853" />
              <circle cx="8" cy="0" r="5" fill="#00C853" />
              <circle cx="0" cy="8" r="5" fill="#00C853" />
              <circle cx="-8" cy="0" r="5" fill="#00C853" />
            </g>
            {/* card slot */}
            <rect x="40" y="56" width="100" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.50" />
            <rect x="40" y="58" width="100" height="2" rx="1" fill="#000000" fillOpacity="0.40" />
            {/* card peeking out */}
            <g transform="translate(28 -52)" filter={`url(#${id}-card-shadow)`}>
              <rect width="124" height="78" rx="9" fill="#FFFFFF" />
              <rect width="124" height="3" rx="1.5" fill={`url(#${id}-accent)`} />
              {/* chip */}
              <rect x="10" y="14" width="22" height="18" rx="3" fill="#C5A878" />
              <rect x="13" y="17" width="16" height="12" rx="2" fill="#D8B888" />
              <rect x="14" y="19" width="14" height="1.2" rx="0.5" fill="#9C7E5A" />
              <rect x="14" y="22" width="14" height="1.2" rx="0.5" fill="#9C7E5A" />
              <rect x="14" y="25" width="14" height="1.2" rx="0.5" fill="#9C7E5A" />
              {/* contactless waves */}
              <g transform="translate(40 16)" stroke={`url(#${id}-accent)`} strokeWidth="1.6" fill="none" strokeLinecap="round">
                <path d="M0 4 Q3 0 6 4" />
                <path d="M-2 8 Q3 -2 8 8" opacity="0.65" />
                <path d="M-4 12 Q3 -4 10 12" opacity="0.35" />
              </g>
              {/* number */}
              <text x="10" y="48" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.82">
                4242 ●●●● ●●●● 1245
              </text>
              <text x="10" y="62" fontFamily="Inter, system-ui, sans-serif" fontSize="6.5" fontWeight="700" fill="#061A3A" opacity="0.45">CARDHOLDER</text>
              <text x="10" y="72" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#061A3A">A. RIVERA</text>
              {/* brand */}
              <g transform="translate(98 58)">
                <circle r="6" fill="#EB001B" />
                <circle cx="9" r="6" fill="#F79E1B" />
              </g>
            </g>
          </g>
        )}

        {/* SIDE DEVICE — Dejavoo: physical card floating with contactless tap symbol */}
        {variant === "dejavoo-iposgo" && (
          <g>
            {/* contactless ripples behind card */}
            <g transform="translate(360 280)" opacity="0.5">
              <circle r="48" fill="none" stroke="#0072FF" strokeWidth="1.4">
                <animate attributeName="r" values="20;48;20" dur="2.6s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
              </circle>
              <circle r="36" fill="none" stroke="#0072FF" strokeWidth="1.4">
                <animate attributeName="r" values="14;36;14" dur="2.6s" begin="0.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.65;0;0.65" dur="2.6s" begin="0.4s" repeatCount="indefinite" />
              </circle>
            </g>

            {/* credit card */}
            <g transform="translate(286 210) rotate(-14)" filter={`url(#${id}-deep-shadow)`}>
              <rect width="200" height="124" rx="14" fill={`url(#${id}-header)`} />
              {/* metallic shine */}
              <rect width="200" height="124" rx="14" fill={`url(#${id}-glare)`} />
              {/* chip */}
              <rect x="18" y="42" width="32" height="26" rx="4" fill="#C5A878" />
              <rect x="22" y="46" width="24" height="18" rx="3" fill="#D8B888" />
              <rect x="24" y="49" width="20" height="1.6" rx="0.8" fill="#9C7E5A" />
              <rect x="24" y="54" width="20" height="1.6" rx="0.8" fill="#9C7E5A" />
              <rect x="24" y="59" width="20" height="1.6" rx="0.8" fill="#9C7E5A" />
              {/* contactless on card */}
              <g transform="translate(60 46)" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.9">
                <path d="M0 8 Q4 0 8 8" />
                <path d="M-3 14 Q4 -1 11 14" opacity="0.7" />
              </g>
              {/* number */}
              <text x="18" y="92" fontFamily="ui-monospace, Menlo, monospace" fontSize="13" fontWeight="700" fill="#FFFFFF" opacity="0.95" letterSpacing="1.5">
                ●●●●  ●●●●  ●●●●  1245
              </text>
              <text x="18" y="110" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#FFFFFF" opacity="0.6">VALID THRU 12/29</text>
              {/* brand mark */}
              <g transform="translate(150 95)">
                <circle r="11" fill="#EB001B" />
                <circle cx="16" r="11" fill="#F79E1B" />
              </g>
            </g>
          </g>
        )}

        {/* PHONE — centered */}
        <g transform="translate(86 28)" filter={`url(#${id}-deep-shadow)`}>
          {/* outer frame */}
          <rect width="308" height="584" rx="46" fill={`url(#${id}-frame)`} />
          {/* metallic side highlight */}
          <rect width="308" height="584" rx="46" fill={`url(#${id}-bezel)`} />
          {/* side buttons */}
          <rect x="-2" y="124" width="2.5" height="36" rx="1.2" fill="#1A1F2E" />
          <rect x="-2" y="174" width="2.5" height="56" rx="1.2" fill="#1A1F2E" />
          <rect x="-2" y="246" width="2.5" height="56" rx="1.2" fill="#1A1F2E" />
          <rect x="307.5" y="160" width="2.5" height="80" rx="1.2" fill="#1A1F2E" />
          {/* inner bezel */}
          <rect x="6" y="6" width="296" height="572" rx="40" fill="#000814" />
          {/* screen */}
          <rect x="10" y="10" width="288" height="564" rx="36" fill={`url(#${id}-screen)`} />
          {/* dynamic island / notch */}
          <rect x="118" y="20" width="72" height="22" rx="11" fill="#000814" />
          <circle cx="172" cy="31" r="3" fill="#0A2552" />
          <circle cx="172" cy="31" r="1.2" fill="#0072FF" />

          {/* iOS status bar */}
          <g transform="translate(28 52)" fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fill="#061A3A">
            <text x="0" y="10" fontSize="11">9:41</text>
            {/* signal bars */}
            <g transform="translate(204 1)">
              <rect x="0" y="6" width="2.5" height="4" rx="0.5" />
              <rect x="4" y="4" width="2.5" height="6" rx="0.5" />
              <rect x="8" y="2" width="2.5" height="8" rx="0.5" />
              <rect x="12" y="0" width="2.5" height="10" rx="0.5" />
            </g>
            {/* wifi */}
            <g transform="translate(224 2)" stroke="#061A3A" strokeWidth="1.5" fill="none" strokeLinecap="round">
              <path d="M0 6 Q4 2 8 6" />
              <path d="M-2 4 Q4 -3 10 4" />
              <circle cx="4" cy="9" r="0.8" fill="#061A3A" stroke="none" />
            </g>
            {/* battery */}
            <g transform="translate(242 0)">
              <rect width="22" height="10" rx="2.5" fill="none" stroke="#061A3A" strokeWidth="1" />
              <rect x="2" y="2" width="16" height="6" rx="1.2" fill="#061A3A" />
              <rect x="23" y="3" width="1.6" height="4" rx="0.6" fill="#061A3A" />
            </g>
          </g>

          {/* APP HEADER */}
          <g transform="translate(10 70)">
            <rect width="288" height="100" fill={`url(#${id}-header)`} />
            {/* header decorative blur */}
            <circle cx="244" cy="20" r="40" fill="#FFFFFF" fillOpacity="0.06" />
            <circle cx="40" cy="90" r="34" fill="#FFFFFF" fillOpacity="0.04" />

            {/* hamburger */}
            <g transform="translate(20 22)" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" opacity="0.85">
              <line x1="0" y1="0" x2="14" y2="0" />
              <line x1="0" y1="5" x2="14" y2="5" />
              <line x1="0" y1="10" x2="10" y2="10" />
            </g>
            {/* brand badge */}
            <g transform="translate(122 16)">
              <rect x="0" y="0" width="6" height="20" rx="3" fill="#FFFFFF" fillOpacity="0.9" />
              <rect x="10" y="0" width="6" height="20" rx="3" fill="#FFFFFF" fillOpacity="0.6" />
              <text x="24" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#FFFFFF" letterSpacing="0.6">
                {variant === "valor-pay" ? "VALOR PAY" : variant === "dejavoo-iposgo" ? "DEJAVOO iPOSgo" : "CLOVER GO"}
              </text>
            </g>
            {/* avatar */}
            <g transform="translate(248 14)">
              <circle r="14" fill="#FFFFFF" fillOpacity="0.18" />
              <text y="4" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#FFFFFF">
                {theme.tag}
              </text>
            </g>

            {/* greeting */}
            <text x="20" y="66" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="#FFFFFF" fillOpacity="0.78">
              Good morning, Alex
            </text>
            <text x="20" y="88" fontFamily="Inter, system-ui, sans-serif" fontSize="17" fontWeight="800" fill="#FFFFFF">
              {theme.tagline}
            </text>
          </g>

          {/* MAIN AMOUNT CARD — overlapping header */}
          <g transform="translate(34 160)" filter={`url(#${id}-card-shadow)`}>
            <rect width="240" height="100" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.06" />
            {/* top tab */}
            <rect width="240" height="4" rx="2" fill={`url(#${id}-accent)`} />
            <text x="18" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" fillOpacity="0.55" letterSpacing="0.6">
              CHARGE AMOUNT
            </text>
            <text x="18" y="64" fontFamily="Inter, system-ui, sans-serif" fontSize="30" fontWeight="800" fill="#061A3A">
              $1,245
              <tspan fontSize="20" fill="#061A3A" fillOpacity="0.6">.00</tspan>
            </text>
            {/* USD chip */}
            <g transform="translate(186 22)">
              <rect width="44" height="22" rx="11" fill={`url(#${id}-accent)`} />
              <text x="22" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">
                USD
              </text>
            </g>
            {/* tiny chart spark */}
            <g transform="translate(18 76)">
              <polyline points="0,8 14,4 28,6 42,2 56,5 70,1 84,3" fill="none" stroke={`url(#${id}-accent)`} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <text x="100" y="8" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">
                +18.2%
              </text>
              <text x="158" y="8" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.5">
                vs last week
              </text>
            </g>
          </g>

          {/* QUICK ACTION ROW */}
          <g transform="translate(34 274)">
            {[
              { l: "Charge", primary: true },
              { l: "Invoice" },
              { l: "Link" },
              { l: "Refund" },
            ].map((a, i) => (
              <g key={a.l} transform={`translate(${i * 60} 0)`}>
                <rect width="52" height="52" rx="14" fill={a.primary ? `url(#${id}-accent)` : "#FFFFFF"} stroke="#061A3A" strokeOpacity={a.primary ? 0 : 0.08} />
                {/* glyph */}
                <g transform="translate(26 22)" stroke={a.primary ? "#FFFFFF" : "#0072FF"} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  {a.l === "Charge" && <path d="M-7 0 H7 M-3 -4 V4 M3 -4 V4" />}
                  {a.l === "Invoice" && (
                    <g>
                      <rect x="-6" y="-7" width="12" height="14" rx="1.5" />
                      <line x1="-3" y1="-2" x2="3" y2="-2" />
                      <line x1="-3" y1="2" x2="3" y2="2" />
                    </g>
                  )}
                  {a.l === "Link" && (
                    <g>
                      <path d="M-6 0 a4 4 0 0 1 4 -4 h4 a4 4 0 0 1 0 8 h-1" />
                      <path d="M6 0 a4 4 0 0 1 -4 4 h-4 a4 4 0 0 1 0 -8 h1" />
                    </g>
                  )}
                  {a.l === "Refund" && (
                    <g>
                      <path d="M-5 -4 a6 6 0 1 1 -2 8" />
                      <path d="M-7 -6 L-5 -4 L-3 -7" />
                    </g>
                  )}
                </g>
                <text x="26" y="68" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">
                  {a.l}
                </text>
              </g>
            ))}
          </g>

          {/* TAP TO PAY CTA */}
          <g transform="translate(34 372)">
            <rect width="240" height="92" rx="18" fill="#EAF3FF" />
            <rect width="240" height="92" rx="18" fill={`url(#${id}-accent)`} fillOpacity="0.08" />
            <g transform="translate(120 38)">
              <circle r="22" fill="none" stroke="#0072FF" strokeWidth="2" strokeOpacity="0.4">
                <animate attributeName="r" values="22;40;22" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle r="20" fill={`url(#${id}-accent)`} />
              {/* contactless waves */}
              <g stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round">
                <path d="M-7 -3 Q-3 -9 1 -3 Q5 -9 9 -3" />
                <path d="M-5 5 Q-1 -1 3 5" opacity="0.65" />
              </g>
            </g>
            <text x="120" y="82" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">
              Hold card or phone near reader
            </text>
          </g>

          {/* RECENT ACTIVITY */}
          <g transform="translate(34 482)">
            <g>
              <text x="0" y="0" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" fillOpacity="0.55">
                RECENT ACTIVITY
              </text>
              <text x="240" y="0" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#0072FF">
                See all
              </text>
            </g>
            {[
              { l: "Atlas Coffee Co", t: "9:14 AM", a: "+$48.20" },
              { l: "Bloom Boutique", t: "9:02 AM", a: "+$129.99" },
              { l: "Riverside Auto", t: "Yesterday", a: "+$642.00" },
            ].map((r, i) => (
              <g key={r.l} transform={`translate(0 ${18 + i * 22})`}>
                <circle cx="10" cy="10" r="10" fill={`url(#${id}-accent)`} fillOpacity={0.95 - i * 0.18} />
                <text x="10" y="14" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">
                  {r.l.charAt(0)}
                </text>
                <text x="26" y="9" fontFamily="Inter, system-ui, sans-serif" fontSize="10.5" fontWeight="800" fill="#061A3A">
                  {r.l}
                </text>
                <text x="26" y="19" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" fillOpacity="0.45">
                  {r.t}
                </text>
                <text x="240" y="14" textAnchor="end" fontFamily="ui-monospace, Menlo, monospace" fontSize="10.5" fontWeight="800" fill="#0072FF">
                  {r.a}
                </text>
              </g>
            ))}
          </g>

          {/* home indicator */}
          <rect x="124" y="556" width="60" height="4" rx="2" fill="#061A3A" fillOpacity="0.22" />

          {/* screen glare */}
          <rect x="10" y="10" width="288" height="180" rx="36" fill={`url(#${id}-glare)`} pointerEvents="none" />
        </g>
      </svg>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   APP FEATURE SECTION — full-width row
   ──────────────────────────────────────────────────────────── */
function AppFeatureSection({ app, index }) {
  const imageRight = index % 2 === 0;
  return (
    <Reveal>
      <div
        className="group relative rounded-3xl overflow-hidden border border-line bg-white shadow-[0_20px_50px_-30px_rgba(6,26,58,0.30)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_36px_70px_-26px_rgba(0,114,255,0.30)] hover:border-[var(--swipe-accent)]/30"
        data-testid={`mobile-app-${app.slug}`}
      >
        <div className={`grid lg:grid-cols-2 items-stretch ${imageRight ? "" : "lg:[&>*:first-child]:order-2"}`}>
          {/* IMAGE BLOCK */}
          <div className={`relative min-h-[460px] lg:min-h-[600px] flex items-center justify-center p-8 lg:p-12 overflow-hidden ${imageRight ? "lg:order-2" : "lg:order-1"}`}
               style={{ background: "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 55%, #EAF3FF 100%)" }}>
            {/* dotted backdrop */}
            <svg className="absolute inset-0 w-full h-full opacity-40" aria-hidden="true">
              <defs>
                <pattern id={`md-dots-${app.slug}`} width="16" height="16" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.18" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#md-dots-${app.slug})`} />
            </svg>
            {/* glow blobs */}
            <div aria-hidden="true" className="absolute -top-20 -right-12 w-[300px] h-[300px] rounded-full" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(70px)" }} />
            <div aria-hidden="true" className="absolute -bottom-20 -left-12 w-[280px] h-[280px] rounded-full" style={{ background: "rgba(6,26,58,0.06)", filter: "blur(70px)" }} />

            {/* brand chip */}
            <div className="absolute top-6 left-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--swipe-navy)] text-white text-[10.5px] uppercase tracking-[0.18em] font-extrabold shadow-[0_10px_22px_-8px_rgba(6,26,58,0.55)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
              {app.accent}
            </div>

            <div className="relative w-full transition-transform duration-700 group-hover:-translate-y-2">
              <PhoneMockup variant={app.slug} />
            </div>
          </div>

          {/* TEXT BLOCK */}
          <div className={`flex flex-col justify-center p-8 sm:p-10 lg:p-14 ${imageRight ? "lg:order-1" : "lg:order-2"}`}>
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="text-[11px] uppercase tracking-[0.22em] font-extrabold text-[var(--swipe-accent)]">
                {app.accent}
              </span>
            </div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[2.85rem] font-extrabold text-[var(--swipe-navy)] leading-[1.05] tracking-[-0.025em]">
              {app.title}
            </h2>
            <p className="mt-6 text-[16px] text-slate-600 leading-[1.8] max-w-[540px]">
              {app.description}
            </p>

            {/* check mark feature boxes */}
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-[560px]">
              {app.features.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-3 rounded-xl border border-[var(--swipe-accent)]/15 bg-[var(--swipe-accent-soft)] px-4 py-3 transition-all duration-300 hover:border-[var(--swipe-accent)]/40 hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-14px_rgba(0,114,255,0.35)]"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--swipe-accent)] text-white shadow-[0_8px_18px_-6px_rgba(0,114,255,0.55)] shrink-0">
                    <Check className="w-4 h-4" strokeWidth={3.2} />
                  </span>
                  <span className="text-[13.5px] font-bold text-[var(--swipe-navy)] leading-tight">
                    {f}
                  </span>
                </li>
              ))}
            </ul>

            {/* store buttons */}
            <div className="mt-9 flex flex-wrap gap-3">
              <StoreButton store="apple" />
              <StoreButton store="google" />
            </div>
          </div>
        </div>

        {/* hover accent line */}
        <span aria-hidden="true" className="absolute top-0 left-12 right-12 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
      </div>
    </Reveal>
  );
}

/* ────────────────────────────────────────────────────────────
   PAYMENT METHOD CARD
   ──────────────────────────────────────────────────────────── */
function PaymentMethodCard({ method }) {
  return (
    <div
      data-testid={`payment-method-${method.slug}`}
      className="group relative h-full rounded-2xl border border-line bg-white p-6 lg:p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_28px_56px_-26px_rgba(0,114,255,0.35)] shadow-[0_14px_36px_-30px_rgba(6,26,58,0.30)] overflow-hidden"
    >
      {/* hover top accent */}
      <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />

      <div className="relative h-[120px] w-full rounded-xl bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/15 mb-5 overflow-hidden flex items-center justify-center p-5 transition-transform duration-300 group-hover:scale-[1.02]">
        {/* dotted background */}
        <svg className="absolute inset-0 w-full h-full opacity-40" aria-hidden="true">
          <defs>
            <pattern id={`pm-dots-${method.slug}`} width="12" height="12" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.16" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#pm-dots-${method.slug})`} />
        </svg>
        <img
          src={method.img}
          alt={method.name}
          className="relative max-h-[80px] max-w-[80%] object-contain transition-transform duration-300 group-hover:scale-[1.04]"
          loading="lazy"
        />
      </div>
      <h3 className="font-display-tight text-[17px] font-extrabold text-[var(--swipe-navy)] leading-tight tracking-[-0.01em]">
        {method.name}
      </h3>
      <p className="mt-2.5 text-[13.5px] text-slate-600 leading-[1.7]">
        {method.description}
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   USE CASE BOX
   ──────────────────────────────────────────────────────────── */
function UseCaseBox({ icon: Icon, title, text, index }) {
  return (
    <div
      data-testid={`mobile-usecase-${index}`}
      className="group cursor-pointer h-full relative rounded-2xl p-7 lg:p-8 border border-[var(--swipe-accent)]/20 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.45)] overflow-hidden"
      style={{
        background: "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 55%, #EAF3FF 100%)",
      }}
    >
      {/* Accent left bar */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full bg-[var(--swipe-accent)] opacity-70 group-hover:opacity-100 group-hover:top-4 group-hover:bottom-4 transition-all duration-300"
      />
      {/* Soft glow on hover */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-44 h-44 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "rgba(0,114,255,0.22)", filter: "blur(40px)" }}
      />
      <div className="relative">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]"
          style={{
            background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
            boxShadow: "0 12px 28px -10px rgba(0,114,255,0.55)",
          }}
        >
          <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <h3 className="mt-6 font-display text-[18px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
          {title}
        </h3>
        <p className="mt-3 text-[14.5px] text-slate-600 leading-[1.75]">{text}</p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   MAIN PAGE
   ──────────────────────────────────────────────────────────── */
export default function MobileDevices() {
  return (
    <div data-testid="mobile-devices-page">
      <Seo {...getSeoForPath("/mobile-devices")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Mobile Devices</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Products
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  Mobile{" "}
                  <span className="text-[var(--swipe-accent)]">Devices</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Accept payments anywhere your business goes with flexible mobile payment solutions.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="mobile-hero-cta-info"
                  >
                    Request Mobile Device Info <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/savings-analysis"
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="mobile-hero-cta-savings"
                  >
                    Free Savings Analysis
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <AnywhereIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO — centered headline + paragraph with reveal animations */}
      <section className="relative bg-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,243,255,0.9) 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[200px] rounded-full pointer-events-none opacity-40"
          style={{ background: "rgba(0,114,255,0.10)", filter: "blur(80px)" }}
        />
        <div className="container-swipe relative pt-8 pb-12 lg:pt-10 lg:pb-16 text-center">
          <Reveal
            variant="words"
            as="h2"
            className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.08] tracking-[-0.02em] max-w-5xl mx-auto"
            threshold={0.05}
          >
            {"Mobile Payment Solutions Built for Flexibility".split(" ").map((word, i, arr) => (
              <span key={i}>
                <span className="word"><span>{word}</span></span>
                {i < arr.length - 1 ? " " : null}
              </span>
            ))}
          </Reveal>

          <Reveal variant="underline" delay={500}>
            <span
              aria-hidden="true"
              className="block mx-auto mt-6 h-[3px] w-24 rounded-full"
              style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }}
            />
          </Reveal>

          <Reveal delay={650}>
            <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
              Swipetronics helps businesses accept secure payments beyond the counter with mobile
              payment devices, handheld terminals, tap-to-pay options, and flexible checkout tools.
              Whether you sell at the table, on delivery, at events, or on the go, mobile devices
              help your business serve customers wherever the sale happens.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MOBILE APPS — 3 large stacked feature sections */}
      <section className="relative bg-white" data-testid="mobile-apps-section">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 bottom-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(234,243,255,0.0) 0%, rgba(234,243,255,0.55) 25%, rgba(234,243,255,0.55) 75%, rgba(234,243,255,0.0) 100%)",
          }}
        />
        <div className="container-swipe relative pb-24 lg:pb-28">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14 lg:mb-16">
              <div className="overline mb-3 inline-flex justify-center">Mobile Apps & Devices</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                Apps Built for On-the-Go Business
              </h2>
              <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.8] max-w-[820px] mx-auto">
                Three flexible mobile payment apps designed to help you sell, get paid, and stay
                connected to your business anywhere.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-10 lg:gap-12 max-w-[1280px] mx-auto">
            {MOBILE_APPS.map((app, i) => (
              <AppFeatureSection key={app.slug} app={app} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PAYMENT METHODS — 8 boxes */}
      <section
        className="relative bg-gradient-to-b from-[var(--swipe-muted)] to-white border-y border-line overflow-hidden"
        data-testid="payment-methods-section"
      >
        <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.08) 0%, transparent 65%)" }} />
        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--swipe-accent)]">
                  Payment Options
                </span>
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
              </div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1] tracking-[-0.02em]">
                Payment <span className="text-[var(--swipe-accent)]">Methods</span>
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[720px] mx-auto">
                We offer multiple payment options to suit your customers&rsquo; preferences.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {PAYMENT_METHODS.map((m, i) => (
              <Reveal key={m.slug} delay={(i % 4) * 70}>
                <PaymentMethodCard method={m} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES — BUILT FOR BUSINESSES THAT MOVE */}
      <section className="bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15">
        <div className="container-swipe py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center">Industries & Use Cases</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                Built for Businesses That Move
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[780px] mx-auto">
                From restaurants and delivery routes to pop-up shops and field services,
                Swipetronics mobile payment options keep your checkout flexible wherever the day
                takes you.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={(i % 3) * 70}>
                <UseCaseBox icon={u.icon} title={u.title} text={u.text} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 STEPS */}
      <section className="relative bg-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,243,255,0.9) 0%, transparent 70%)",
          }}
        />
        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center">How It Works</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                Mobile Payments Made Simple
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[780px] mx-auto">
                A simple four-step path from picking the right mobile option to serving your
                customers anywhere.
              </p>
            </div>
          </Reveal>

          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {/* Connector line behind steps on lg */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute top-[60px] left-[10%] right-[10%] h-[2px] pointer-events-none"
              style={{
                background:
                  "repeating-linear-gradient(90deg, rgba(0,114,255,0.35) 0 8px, transparent 8px 16px)",
              }}
            />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.title} delay={i * 90}>
                  <div
                    className="relative h-full bg-white border border-line rounded-2xl p-7 lg:p-8 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)]"
                    data-testid={`mobile-step-${i + 1}`}
                  >
                    {/* Numbered ring */}
                    <div className="relative mx-auto w-[120px] h-[120px] flex items-center justify-center">
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            "conic-gradient(from 180deg, rgba(0,114,255,0.25), rgba(0,114,255,0.0) 60%)",
                        }}
                      />
                      <div
                        className="relative w-[96px] h-[96px] rounded-full flex items-center justify-center bg-white"
                        style={{
                          boxShadow:
                            "inset 0 0 0 2px rgba(0,114,255,0.18), 0 18px 36px -16px rgba(0,114,255,0.40)",
                        }}
                      >
                        <Icon className="w-8 h-8 text-[var(--swipe-accent)]" strokeWidth={2} />
                      </div>
                      <span
                        className="absolute -top-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-extrabold text-white"
                        style={{
                          background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
                          boxShadow: "0 10px 22px -8px rgba(0,114,255,0.65)",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 font-display-tight text-[17px] lg:text-[18px] font-extrabold text-[var(--swipe-navy)] leading-tight">
                      {step.title}
                    </h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 80% 0%, rgba(0,114,255,0.20) 0%, transparent 60%)",
          }}
        />
        <div className="container-swipe py-16 md:py-20 relative">
          <Reveal>
            <div className="grid lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
              <div>
                <div className="overline mb-3" style={{ color: "#7DB6FF" }}>Mobile Payments</div>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-white leading-tight">
                  Need Mobile Payment Options?
                </h2>
                <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-2xl">
                  Swipetronics can help you choose the right mobile payment device or app-based
                  solution for your business.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:justify-end">
                <Link to="/get-started" className="btn-primary-swipe" data-testid="mobile-cta-getstarted">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="btn-outline-light"
                  data-testid="mobile-cta-info"
                >
                  Request Mobile Device Info
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
